import { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { CheckCircle, Clock, Mail, MapPin, ShieldAlert, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString('pt-BR') : '-';
}

export default function PublicEmailApprovalPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [approval, setApproval] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchApproval();
  }, [token]);

  const fetchApproval = async () => {
    try {
      const response = await axios.get(`${API}/public/email-approval/${token}`);
      setApproval(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nao foi possivel carregar a solicitacao');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/public/email-approval/${token}/approve`);
      setSuccess(response.data);
      toast.success(response.data?.message || 'Solicitacao aprovada');
      await fetchApproval();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nao foi possivel aprovar a solicitacao');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!approval) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="max-w-xl w-full bg-white rounded-3xl border border-border/40 p-10 shadow-xl text-center">
          <h1 className="text-2xl font-manrope font-bold tracking-tight mb-3">Solicitacao indisponivel</h1>
          <p className="text-muted-foreground">O link pode estar invalido ou a solicitacao nao existe mais.</p>
        </div>
      </div>
    );
  }

  const isPending = approval.status === 'pending';

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/40 bg-white/60 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-6 flex items-center gap-3">
          <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
            <ShieldAlert className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-2xl font-manrope font-bold tracking-tight">Aprovacao de reuniao</h1>
            <p className="text-sm text-muted-foreground">Revise os dados da solicitacao antes de aprovar</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-10 space-y-8">
        <div className="bg-white rounded-3xl border border-border/40 p-8 shadow-sm">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-3xl font-manrope font-bold tracking-tight">{approval.meeting_name}</h2>
              <p className="text-muted-foreground mt-2">Solicitacao enviada para {approval.approver_email || 'aprovador cadastrado'}</p>
            </div>
            <span className={`text-xs font-medium px-3 py-2 rounded-full ${
              isPending
                ? 'bg-amber-100 text-amber-700'
                : approval.status === 'approved'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
            }`}>
              {isPending ? 'Pendente' : approval.status === 'approved' ? 'Aprovada' : 'Rejeitada'}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="rounded-2xl bg-gray-50 p-4">
              <p className="text-muted-foreground mb-1 flex items-center gap-2"><User className="w-4 h-4" /> Solicitante</p>
              <p className="font-medium">{approval.organizer_name || '-'}</p>
              <p className="text-muted-foreground break-all">{approval.organizer_email || '-'}</p>
            </div>
            <div className="rounded-2xl bg-gray-50 p-4">
              <p className="text-muted-foreground mb-1 flex items-center gap-2"><MapPin className="w-4 h-4" /> Sala</p>
              <p className="font-medium">{approval.room_name || '-'}</p>
              <p className="text-muted-foreground">Origem: {approval.source || '-'}</p>
            </div>
            <div className="rounded-2xl bg-gray-50 p-4">
              <p className="text-muted-foreground mb-1 flex items-center gap-2"><Clock className="w-4 h-4" /> Inicio</p>
              <p className="font-medium">{formatDateTime(approval.start_time)}</p>
            </div>
            <div className="rounded-2xl bg-gray-50 p-4">
              <p className="text-muted-foreground mb-1 flex items-center gap-2"><Clock className="w-4 h-4" /> Fim</p>
              <p className="font-medium">{formatDateTime(approval.end_time)}</p>
            </div>
            <div className="rounded-2xl bg-gray-50 p-4 md:col-span-2">
              <p className="text-muted-foreground mb-1 flex items-center gap-2"><Mail className="w-4 h-4" /> Participantes</p>
              <p className="font-medium break-all">{approval.attendees?.length ? approval.attendees.join(', ') : 'Nenhum participante adicional informado'}</p>
            </div>
          </div>

          {approval.rejection_reason && (
            <div className="mt-6 rounded-2xl border border-red-300 bg-red-50 p-4 text-sm text-red-700">
              <p className="font-semibold mb-1">Motivo da rejeicao</p>
              <p>{approval.rejection_reason}</p>
            </div>
          )}

          {success && (
            <div className="mt-6 rounded-2xl border border-green-300 bg-green-50 p-4 text-sm text-green-700">
              <p className="font-semibold mb-1">Solicitacao aprovada</p>
              <p>{success.message}</p>
            </div>
          )}

          {isPending && (
            <div className="mt-8 flex justify-end">
              <Button onClick={handleApprove} disabled={processing} className="rounded-full h-12 px-8">
                <CheckCircle className="w-4 h-4 mr-2" />
                {processing ? 'Aprovando...' : 'Aprovar reuniao'}
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
